import { createTranslator, normalizeLocale, supportedLocales } from '../i18n/index.mjs'

const SITE_URL = 'https://www.starapp.fun/happypet'
const DEFAULT_TITLE = 'Happy Pets | AI Companion, Self-Discovery Tests & Pet Widgets'
const DEFAULT_DESCRIPTION = 'Meet an AI pet companion for daily reflection, personality and self-discovery tests, tarot, fortune, dream insights, interactive widgets, and co-raising with friends.'
const DEFAULT_KEYWORDS = 'happy pets, AI companion, virtual pet app, pet widget, self discovery tests, personality test, mbti test, sbti test, iq test, holland career test, attachment style test, ai tarot, daily fortune, dream interpretation, co raise pet, ios pet widget, android pet widget'
const DEFAULT_IMAGE = `${SITE_URL}/favicon.ico`
const DEFAULT_ROBOTS = 'index,follow,max-image-preview:large'

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Happy Pets',
  applicationCategory: 'GameApplication',
  operatingSystem: 'iOS, Android',
  description: DEFAULT_DESCRIPTION,
  keywords: DEFAULT_KEYWORDS,
  url: `${SITE_URL}/`,
  image: DEFAULT_IMAGE,
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD'
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    ratingCount: '1200000'
  },
  downloadUrl: [
    'https://apps.apple.com/app/id6742995719',
    'https://play.google.com/store/apps/details?id=happy.pet.com.ai'
  ],
  publisher: {
    '@type': 'Organization',
    name: 'Happy Pets'
  }
}

function ensureMeta(selector, attributes, content) {
  let element = document.head.querySelector(selector)

  if (!element) {
    element = document.createElement('meta')
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value))
    document.head.appendChild(element)
  }

  element.setAttribute('content', content)
}

function ensureLink(selector, rel, href) {
  let element = document.head.querySelector(selector)

  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', rel)
    document.head.appendChild(element)
  }

  element.setAttribute('href', href)
  return element
}

function ensureJsonLd(schema) {
  const existing = document.head.querySelector('#seo-json-ld')

  if (!schema) {
    existing?.remove()
    return
  }

  const script = existing || document.createElement('script')
  script.id = 'seo-json-ld'
  script.type = 'application/ld+json'
  script.textContent = JSON.stringify(schema)

  if (!existing) {
    document.head.appendChild(script)
  }
}

function toCanonical(path = '/', locale = 'en') {
  const normalizedPath = path === '/' ? '/' : path.replace(/\/$/, '')
  const url = new URL(`${SITE_URL}${normalizedPath}`)
  url.searchParams.set('lang', locale)
  return url.toString()
}

function localizedSchema(description, keywords, canonical) {
  return {
    ...homeSchema,
    description,
    keywords,
    url: canonical
  }
}

export function getRouteSeo(route, locale) {
  const normalizedLocale = normalizeLocale(locale) || 'zh'
  const t = createTranslator(normalizedLocale)
  const routeSeo = route?.meta?.seo || {}
  const isHome = route?.name === 'Home'
  const isInvite = route?.name === 'Invite' || route?.name === 'CoParentInvite'
  const title = isHome
    ? t('seo.home.title')
    : isInvite ? t('seo.invite.title') : (routeSeo.title || t('seo.home.title'))
  const description = isHome
    ? t('seo.home.description')
    : isInvite ? t('seo.invite.description') : (routeSeo.description || t('seo.home.description'))
  const keywords = isHome ? t('seo.home.keywords') : (routeSeo.keywords || t('seo.home.keywords'))
  const canonicalPath = routeSeo.canonical || route?.path || '/'
  const canonical = toCanonical(canonicalPath, normalizedLocale)
  const robots = routeSeo.robots || DEFAULT_ROBOTS
  const image = routeSeo.image || DEFAULT_IMAGE
  const type = routeSeo.type || 'website'

  return {
    ...routeSeo,
    title,
    description,
    keywords,
    canonical,
    robots,
    image,
    type,
    lang: normalizedLocale,
    schema: isHome ? localizedSchema(description, keywords, canonical) : routeSeo.schema
  }
}

function ensureAlternateLinks(path) {
  for (const locale of supportedLocales) {
    const selector = `link[rel="alternate"][hreflang="${locale}"]`
    ensureLink(selector, 'alternate', toCanonical(path, locale)).setAttribute('hreflang', locale)
  }

  const selector = 'link[rel="alternate"][hreflang="x-default"]'
  ensureLink(selector, 'alternate', toCanonical(path, 'zh')).setAttribute('hreflang', 'x-default')
}

export function applyRouteSeo(route, locale = 'zh') {
  if (typeof document === 'undefined') {
    return
  }

  const routeSeo = getRouteSeo(route, locale)
  const { title, description, keywords, canonical, robots, image, type } = routeSeo

  document.title = title
  document.documentElement.lang = routeSeo.lang === 'zh' ? 'zh-CN' : routeSeo.lang

  ensureMeta('meta[name="description"]', { name: 'description' }, description)
  ensureMeta('meta[name="keywords"]', { name: 'keywords' }, keywords)
  ensureMeta('meta[name="robots"]', { name: 'robots' }, robots)
  ensureMeta('meta[property="og:type"]', { property: 'og:type' }, type)
  ensureMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, 'Happy Pets')
  ensureMeta('meta[property="og:locale"]', { property: 'og:locale' }, routeSeo.lang.replace('-', '_'))
  ensureMeta('meta[property="og:title"]', { property: 'og:title' }, routeSeo.ogTitle || title)
  ensureMeta('meta[property="og:description"]', { property: 'og:description' }, routeSeo.ogDescription || description)
  ensureMeta('meta[property="og:url"]', { property: 'og:url' }, canonical)
  ensureMeta('meta[property="og:image"]', { property: 'og:image' }, image)
  ensureMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary')
  ensureMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, routeSeo.twitterTitle || title)
  ensureMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, routeSeo.twitterDescription || description)
  ensureMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, image)
  ensureLink('link[rel="canonical"]', 'canonical', canonical)
  ensureAlternateLinks(routeSeo.canonical ? new URL(routeSeo.canonical).pathname.replace('/happypet', '') || '/' : route?.path || '/')
  ensureJsonLd(routeSeo.schema || homeSchema)
}

export const defaultHomeSeo = {
  title: DEFAULT_TITLE,
  description: DEFAULT_DESCRIPTION,
  keywords: DEFAULT_KEYWORDS,
  canonical: '/',
  schema: homeSchema
}
